import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError, formatModelNumber } from './modelInputContract';
import {
  type CrystalModelResult,
  type SaturationState,
  type SolubilityBracket,
  type SolutePreset,
  bracketSolubility,
  runCrystalModel,
  solubilityRangeMessage,
} from './solutionsLab';

/**
 * Explore calculations for the Solutions lab (chemistry/CLAUDE.md "Interactive calculation
 * standard"). Both use the reviewed solubility curves and C₁V₁ = C₂V₂ from `solutionsLab.ts`; the
 * worked text is built from the same numbers that drive the graph, beakers and 3D scene.
 */

// ---------- Crystallisation on cooling ----------

export interface CrystalSetup {
  waterMassG: number;
  soluteMassG: number;
  hotTemperatureC: number;
  coolTemperatureC: number;
}

export type CrystalInput = keyof CrystalSetup;

/** Temperature limits equal the reviewed data range (0–100 °C for every curve). */
export const CRYSTAL_FIELDS: ModelField<CrystalInput>[] = [
  { key: 'waterMassG', label: 'Mass of water', unit: 'g', min: 50, max: 500, step: 1 },
  { key: 'soluteMassG', label: 'Mass of solute', unit: 'g', min: 1, max: 500, step: 1 },
  { key: 'hotTemperatureC', label: 'Hot temperature', unit: '°C', min: 0, max: 100, step: 1 },
  { key: 'coolTemperatureC', label: 'Cool to', unit: '°C', min: 0, max: 100, step: 1 },
];

export const DEFAULT_CRYSTAL_SETUP: CrystalSetup = { waterMassG: 100, soluteMassG: 80, hotTemperatureC: 60, coolTemperatureC: 20 };

/** The solubility graph's vertical scale; a load above it is reported, not drawn off the chart. */
export const SOLUBILITY_GRAPH_MAX_G_PER_100G = 250;

/** The 3D beaker is drawn as a 600 mL beaker; the liquid level follows the water mass up to this. */
export const SOLUTIONS_BEAKER_CAPACITY_G = 600;

export const COOLING_ORDER_MESSAGE = 'Cool to must be at or below the hot temperature: cooling cannot warm the solution.';

export interface CrystalExploreResult {
  model: CrystalModelResult;
  hot: SolubilityBracket;
  cool: SolubilityBracket;
  /** Solid that never dissolves, because the mass added is above the hot limit. */
  undissolvedHotG: number;
  /** The mass added expressed per 100 g water, the basis of the solubility graph. */
  loadPer100gWater: number;
  hotState: SaturationState;
  steps: WorkedStep[];
  result: string;
  limitNote: string | null;
}

const grams = (value: number) => `${formatModelNumber(value, 1)} g`;
const solubility = (value: number) => formatModelNumber(value, 2);

function interpolationWorking(bracket: SolubilityBracket, temperatureC: number): string {
  const { lower, upper, value } = bracket;
  const t = formatModelNumber(temperatureC, 1);
  if (temperatureC === lower.temperatureC || temperatureC === upper.temperatureC) {
    return `s(${t} °C) = ${solubility(value)} g per 100 g water (reviewed data point)`;
  }
  return (
    `s = ${solubility(lower.gramsPer100gWater)} + (${t} − ${lower.temperatureC}) ÷ (${upper.temperatureC} − ${lower.temperatureC})` +
    ` × (${solubility(upper.gramsPer100gWater)} − ${solubility(lower.gramsPer100gWater)}) = ${solubility(value)} g per 100 g water`
  );
}

const STATE_NOTES: Record<SaturationState, string> = {
  unsaturated: 'unsaturated: all of it dissolves',
  saturated: 'saturated: right at the limit',
  'excess-solid': 'saturated, with solid left over',
};

/** Worked crystallisation for one learner setup. Throws on invalid input (fail closed). */
export function calculateCrystallisation(preset: SolutePreset, setup: CrystalSetup): CrystalExploreResult {
  const error = firstModelError(CRYSTAL_FIELDS, setup);
  if (error) throw new Error(error);
  if (setup.coolTemperatureC > setup.hotTemperatureC) throw new Error(COOLING_ORDER_MESSAGE);
  const hot = bracketSolubility(preset, setup.hotTemperatureC);
  const cool = bracketSolubility(preset, setup.coolTemperatureC);
  if (!hot || !cool) throw new Error(solubilityRangeMessage(preset));

  const model = runCrystalModel({ preset, ...setup });
  const undissolvedHotG = Math.max(0, setup.soluteMassG - model.hotLimitG);
  const loadPer100gWater = (setup.soluteMassG * 100) / setup.waterMassG;
  const water = grams(setup.waterMassG);
  const coolDissolvedG = model.dissolvedHotG - model.crystalsOnCoolingG;
  const hasCrystals = model.crystalsOnCoolingG > 0;

  const steps: WorkedStep[] = [
    {
      id: 'solubility-hot',
      title: `Solubility at ${formatModelNumber(setup.hotTemperatureC, 1)} °C`,
      formula: 's = s₁ + (T − T₁) ÷ (T₂ − T₁) × (s₂ − s₁)',
      working: interpolationWorking(hot, setup.hotTemperatureC),
      status: 'done',
    },
    {
      id: 'solubility-cool',
      title: `Solubility at ${formatModelNumber(setup.coolTemperatureC, 1)} °C`,
      formula: 's = s₁ + (T − T₁) ÷ (T₂ − T₁) × (s₂ − s₁)',
      working: interpolationWorking(cool, setup.coolTemperatureC),
      status: 'done',
    },
    {
      id: 'limit-hot',
      title: 'Most that dissolves when hot',
      formula: 'limit = s × m(water) ÷ 100',
      working: `${solubility(hot.value)} × ${water} ÷ 100 = ${grams(model.hotLimitG)}`,
      status: 'done',
    },
    {
      id: 'limit-cool',
      title: 'Most that stays dissolved when cool',
      formula: 'limit = s × m(water) ÷ 100',
      working: `${solubility(cool.value)} × ${water} ÷ 100 = ${grams(model.coolLimitG)}`,
      status: 'done',
    },
    {
      id: 'dissolved-hot',
      title: 'Dissolved in the hot solution',
      formula: 'dissolved = smaller of (mass added, hot limit)',
      working: `smaller of (${grams(setup.soluteMassG)}, ${grams(model.hotLimitG)}) = ${grams(model.dissolvedHotG)}`,
      status: 'done',
      note: undissolvedHotG > 0 ? `${grams(undissolvedHotG)} stays undissolved` : STATE_NOTES[model.hotState],
    },
    {
      id: 'crystals',
      title: 'Crystals on cooling',
      formula: 'crystals = dissolved hot − cool limit',
      working: hasCrystals
        ? `${grams(model.dissolvedHotG)} − ${grams(model.coolLimitG)} = ${grams(model.crystalsOnCoolingG)}`
        : `${grams(model.dissolvedHotG)} is not above the cool limit of ${grams(model.coolLimitG)}, so 0 g of crystals form`,
      status: 'current',
    },
  ];

  const result = hasCrystals
    ? `Result: ${grams(model.crystalsOnCoolingG)} of ${preset.formula} crystals form; ${grams(coolDissolvedG)} stays dissolved at ${formatModelNumber(setup.coolTemperatureC, 1)} °C`
    : `Result: no crystals form; all ${grams(model.dissolvedHotG)} stays dissolved at ${formatModelNumber(setup.coolTemperatureC, 1)} °C`;

  const notes: string[] = [];
  if (undissolvedHotG > 0) {
    notes.push(
      `Only ${grams(model.hotLimitG)} can dissolve in ${water} of water at ${formatModelNumber(setup.hotTemperatureC, 1)} °C. The other ${grams(undissolvedHotG)} stays as solid; the model never dissolves more than the reviewed curve allows.`,
    );
  }
  if (loadPer100gWater > SOLUBILITY_GRAPH_MAX_G_PER_100G) {
    notes.push(
      `${formatModelNumber(loadPer100gWater, 1)} g per 100 g water is above the graph scale of ${SOLUBILITY_GRAPH_MAX_G_PER_100G} g, so the graph line stops at the top edge.`,
    );
  }

  return {
    model,
    hot,
    cool,
    undissolvedHotG,
    loadPer100gWater,
    hotState: model.hotState,
    steps,
    result,
    limitNote: notes.length > 0 ? notes.join(' ') : null,
  };
}

// ---------- Crystallisation teaching goal (CT-INTERACT C7) ----------

export const CRYSTAL_QUESTION = 'When a hot solution cools, what decides whether crystals form, and how many?';

/** The learner's solute and setup with the result they produce; task checks read only this. */
export interface CrystalChallengeInput {
  soluteId: SolutePreset['id'];
  setup: CrystalSetup;
  result: CrystalExploreResult;
}

/** Solid at or below this mass counts as none (the model's own saturation tolerance). */
const NO_SOLID_G = 0.05;

/** Teaching tasks, from first observation to the deepest idea. Each check is pure. */
export const CRYSTAL_CHALLENGES: ExploreChallenge<CrystalChallengeInput>[] = [
  {
    id: 'no-crystals',
    prompt: 'Cool a solution by at least 20 °C and get no crystals at all.',
    hint: 'Look at the cool limit: put in less solute than the water can hold when cool.',
    isMet: ({ setup, result }) =>
      setup.hotTemperatureC - setup.coolTemperatureC >= 20 &&
      result.undissolvedHotG <= NO_SOLID_G &&
      result.model.crystalsOnCoolingG <= NO_SOLID_G,
    why: 'Cooling does not always make crystals. At 20 °C, 100 g of water still holds 31.6 g of KNO₃, so a solution with 30 g stays unsaturated and clear.',
  },
  {
    id: 'just-saturated',
    prompt: 'With KNO₃, make the hot solution only just saturated (mass added within 3 % of the hot limit).',
    hint: 'Keep 80 g in 100 g water and find the hot temperature where the curve reaches 80 g: it is between 40 °C and 60 °C.',
    isMet: ({ soluteId, setup, result }) => {
      if (soluteId !== 'potassium-nitrate' || result.model.hotLimitG <= 0) return false;
      const ratio = setup.soluteMassG / result.model.hotLimitG;
      return ratio >= 0.97 && ratio <= 1.03;
    },
    why: 'A just-saturated solution sits on the curve, so crystals first appear as soon as it cools below that temperature. For 80 g of KNO₃ in 100 g of water this is about 47 °C, between 63.9 g at 40 °C and 110 g at 60 °C.',
  },
  {
    id: 'hundred-grams',
    prompt: 'Grow 95–105 g of crystals, with every gram dissolved when hot.',
    hint: 'Crystals = dissolved hot − cool limit, so dissolve about 100 g more than the cool limit.',
    isMet: ({ result }) =>
      result.undissolvedHotG <= NO_SOLID_G && result.model.crystalsOnCoolingG >= 95 && result.model.crystalsOnCoolingG <= 105,
    why: 'Only the solute above the cool limit comes out, not all of the solute you added: 131.6 g of KNO₃ in 100 g of water cooled from 80 °C to 20 °C gives 131.6 − 31.6 = 100 g of crystals.',
  },
];

// ---------- Dilution, C₁V₁ = C₂V₂ ----------

export interface DilutionSetup {
  stockConcentrationMolL: number;
  aliquotVolumeMl: number;
  finalVolumeMl: number;
}

export type DilutionInputKey = keyof DilutionSetup;

export const DILUTION_FIELDS: ModelField<DilutionInputKey>[] = [
  { key: 'stockConcentrationMolL', label: 'Stock concentration C₁', unit: 'mol/L', min: 0.001, max: 5, step: 0.001 },
  { key: 'aliquotVolumeMl', label: 'Aliquot volume V₁', unit: 'mL', min: 1, max: 250, step: 0.1 },
  { key: 'finalVolumeMl', label: 'Final volume V₂', unit: 'mL', min: 5, max: 1000, step: 1 },
];

export const DEFAULT_DILUTION_SETUP: DilutionSetup = { stockConcentrationMolL: 1, aliquotVolumeMl: 25, finalVolumeMl: 250 };

export const DILUTION_ORDER_MESSAGE = 'Final volume V₂ must be at least the aliquot volume V₁: dilution only adds water.';

export interface DilutionExploreResult {
  finalConcentrationMolL: number;
  molesTransferred: number;
  waterAddedMl: number;
  dilutionFactor: number;
  lead: string;
  steps: WorkedStep[];
  result: string;
  limitNote: string | null;
}

/** Three significant figures, as in the Practice answer check (0.100, 1.00, 0.0250). */
export function sig3(value: number): string {
  return value.toLocaleString('en-AU', { minimumSignificantDigits: 3, maximumSignificantDigits: 3 });
}

const mL = (value: number) => `${formatModelNumber(value, 1)} mL`;
const litres = (value: number) => `${sig3(value / 1000)} L`;

/** Worked dilution for one learner setup. Throws on invalid input (fail closed). */
export function calculateDilutionExplore(setup: DilutionSetup): DilutionExploreResult {
  const error = firstModelError(DILUTION_FIELDS, setup);
  if (error) throw new Error(error);
  if (setup.finalVolumeMl < setup.aliquotVolumeMl) throw new Error(DILUTION_ORDER_MESSAGE);
  const { stockConcentrationMolL: c1, aliquotVolumeMl: v1, finalVolumeMl: v2 } = setup;
  const molesTransferred = (c1 * v1) / 1000;
  const finalConcentrationMolL = (c1 * v1) / v2;
  const waterAddedMl = v2 - v1;
  const dilutionFactor = v2 / v1;

  const steps: WorkedStep[] = [
    {
      id: 'moles',
      title: 'Moles in the aliquot',
      formula: 'n = C₁ × V₁',
      working: `${sig3(c1)} mol/L × ${litres(v1)} = ${sig3(molesTransferred)} mol`,
      status: 'done',
    },
    {
      id: 'water',
      title: 'Water added to the mark',
      formula: 'V(water) = V₂ − V₁',
      working: `${mL(v2)} − ${mL(v1)} = ${mL(waterAddedMl)}`,
      status: 'done',
    },
    {
      id: 'concentration',
      title: 'New concentration',
      formula: 'C₂ = C₁ × V₁ ÷ V₂',
      working: `${sig3(c1)} mol/L × ${mL(v1)} ÷ ${mL(v2)} = ${sig3(finalConcentrationMolL)} mol/L`,
      status: 'current',
    },
    {
      id: 'check',
      title: 'Check: the moles do not change',
      formula: 'n = C₂ × V₂',
      working: `${sig3(finalConcentrationMolL)} mol/L × ${litres(v2)} = ${sig3(molesTransferred)} mol`,
      status: 'done',
    },
  ];

  return {
    finalConcentrationMolL,
    molesTransferred,
    waterAddedMl,
    dilutionFactor,
    lead: `Dilution factor = V₂ ÷ V₁ = ${mL(v2)} ÷ ${mL(v1)} = ${formatModelNumber(dilutionFactor, 2)}`,
    steps,
    result: `Result: C₂ = ${sig3(finalConcentrationMolL)} mol/L`,
    limitNote: waterAddedMl === 0 ? 'V₂ equals V₁, so no water is added and C₂ stays equal to C₁.' : null,
  };
}

// ---------- Dilution teaching goal (CT-INTERACT C7) ----------

export const DILUTION_QUESTION = 'When you add water to a solution, what changes and what stays the same?';

/** The learner's setup with the result it produces; task checks read only this. */
export interface DilutionChallengeInput {
  setup: DilutionSetup;
  result: DilutionExploreResult;
}

/** Half of the smallest input step, so a typed value counts as equal to the target. */
const SAME_STOCK_MOL_L = 0.0005;
const SAME_VOLUME_ML = 0.05;
/** A concentration target is met within ± 2 %. */
const CONCENTRATION_TOLERANCE = 0.02;

const near = (value: number, target: number, tolerance: number) => Math.abs(value - target) <= tolerance;
const concentrationNear = (value: number, target: number) => Math.abs(value - target) <= target * CONCENTRATION_TOLERANCE;

/** Teaching tasks, from first observation to the deepest idea. Each check is pure. */
export const DILUTION_CHALLENGES: ExploreChallenge<DilutionChallengeInput>[] = [
  {
    id: 'halve',
    prompt: 'Halve the default C₂ to 0.0500 mol/L by changing only the final volume V₂.',
    hint: 'Keep C₁ = 1.00 mol/L and V₁ = 25 mL. Does doubling the water added do it, or doubling V₂?',
    isMet: ({ setup, result }) =>
      near(setup.stockConcentrationMolL, 1, SAME_STOCK_MOL_L) &&
      near(setup.aliquotVolumeMl, 25, SAME_VOLUME_ML) &&
      concentrationNear(result.finalConcentrationMolL, 0.05),
    why: 'C₂ = C₁V₁ ÷ V₂, so the total volume must double from 250 mL to 500 mL. Doubling only the water added (225 mL to 450 mL, V₂ = 475 mL) gives 0.0526 mol/L, not 0.0500.',
  },
  {
    id: 'plan-standard',
    prompt: 'Make 0.0250 mol/L from a 0.500 mol/L stock in a 100 mL volumetric flask.',
    hint: 'Rearrange C₁V₁ = C₂V₂ to find V₁.',
    isMet: ({ setup, result }) =>
      near(setup.stockConcentrationMolL, 0.5, SAME_STOCK_MOL_L) &&
      near(setup.finalVolumeMl, 100, SAME_VOLUME_ML) &&
      concentrationNear(result.finalConcentrationMolL, 0.025),
    why: 'The dilution factor is C₁ ÷ C₂ = 0.500 ÷ 0.0250 = 20, so V₁ = 100 mL ÷ 20 = 5.00 mL. This is how an analyst plans a standard solution.',
  },
  {
    id: 'same-ratio',
    prompt: 'Get the default C₂ of 0.100 mol/L from the 1.00 mol/L stock with a different aliquot volume V₁.',
    hint: 'Change V₁ and V₂ together.',
    isMet: ({ setup, result }) =>
      near(setup.stockConcentrationMolL, 1, SAME_STOCK_MOL_L) &&
      Math.abs(setup.aliquotVolumeMl - 25) >= 1 &&
      concentrationNear(result.finalConcentrationMolL, 0.1),
    why: 'Only the ratio V₂ ÷ V₁ sets C₂: 10 mL to 100 mL and 25 mL to 250 mL are both 1 in 10 dilutions. The moles differ (0.0100 mol and 0.0250 mol), but the concentration is the same.',
  },
];
